import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, KeyRound, Power } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [open, setOpen] = useState(false);
  const isSuper = user.role === "superadmin";
  const canManage = (u) => isSuper || !["admin", "superadmin"].includes(u.role);

  const load = () => {
    api.get("/users").then(({ data }) => setRows(data));
    api.get("/campuses").then(({ data }) => setCampuses(data));
  };
  useEffect(load, []);
  const cmap = Object.fromEntries(campuses.map((c) => [c.id, c.name]));

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetPin = async (u) => {
    const pin = prompt(`New 6-digit PIN for ${u.name}`);
    if (!pin) return;
    try { await api.patch(`/users/${u.id}`, { pin }); toast.success("PIN reset & account unlocked"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div><h1 className="font-serif text-4xl">Roster</h1><p className="text-[color:var(--ink-2)]">Users, roles &amp; campus assignment.</p></div>
        <button className="btn btn-primary h-11" onClick={() => setOpen(true)} data-testid="add-user"><UserPlus size={16} /> Add User</button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--cream)] text-[color:var(--muted)]"><tr className="text-left"><th className="p-4">Name</th><th className="p-4">Login</th><th className="p-4">Role</th><th className="p-4">Campus</th><th className="p-4">Status</th><th className="p-4"></th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-[color:var(--line)]" data-testid={`user-row-${u.id}`}>
                <td className="p-4 font-medium">{u.name}</td>
                <td className="p-4 text-[color:var(--ink-2)]">{u.email || u.username}</td>
                <td className="p-4 capitalize">{u.role}</td>
                <td className="p-4">{cmap[u.campus_id] || "—"}</td>
                <td className="p-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${u.is_active ? "bg-[color:var(--olive-light)] text-[color:var(--olive)]" : "bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)]"}`}>{u.is_active ? "Active" : "Disabled"}</span></td>
                <td className="p-4 text-right whitespace-nowrap">
                  {u.role === "counter" && <button className="btn btn-ghost h-9 px-3 text-xs mr-1" onClick={() => resetPin(u)} data-testid={`reset-pin-${u.id}`}><KeyRound size={13} /> PIN</button>}
                  {canManage(u) && <button className="btn btn-ghost h-9 px-3 text-xs" onClick={() => toggleActive(u)} data-testid={`toggle-${u.id}`}><Power size={13} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <AddUser campuses={campuses} isAdmin={user.role === "admin" || user.role === "superadmin"} isSuper={isSuper} myCampus={user.campus_id} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function AddUser({ campuses, isAdmin, isSuper, myCampus, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", username: "", role: "counter", campus_id: myCampus || "", password: "", pin: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const isCounter = f.role === "counter";
  const submit = async () => {
    const payload = { name: f.name, role: f.role, campus_id: f.campus_id || null };
    if (isCounter) { payload.username = f.username; payload.pin = f.pin; }
    else { payload.email = f.email; payload.password = f.password; }
    try { await api.post("/users", payload); toast.success("User created"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6" onClick={onClose}>
      <div className="card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-2xl mb-4">Add User</h3>
        <div className="space-y-3">
          <input className="field" placeholder="Full name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="nu-name" />
          <select className="field" value={f.role} onChange={(e) => set("role", e.target.value)} disabled={!isAdmin} data-testid="nu-role">
            <option value="counter">Counter</option>
            {isAdmin && <><option value="coordinator">Campus Coordinator</option><option value="auditor">Auditor</option></>}
            {isSuper && <><option value="admin">Finance Admin</option><option value="superadmin">Super Admin</option></>}
          </select>
          {(isCounter || f.role === "coordinator") && (
            <select className="field" value={f.campus_id} onChange={(e) => set("campus_id", e.target.value)} disabled={!isAdmin} data-testid="nu-campus">
              <option value="">Select campus…</option>
              {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {isCounter ? (
            <>
              <input className="field" placeholder="Username" value={f.username} onChange={(e) => set("username", e.target.value.replace(/\s/g, ""))} data-testid="nu-username" autoCapitalize="none" autoCorrect="off" />
              <input className="field font-mono" placeholder="6-digit PIN" value={f.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="nu-pin" />
            </>
          ) : (
            <>
              <input className="field" placeholder="Email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="nu-email" />
              <input className="field" placeholder="Password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="nu-password" />
            </>
          )}
        </div>
        <div className="flex gap-3 mt-5"><button className="btn btn-ghost flex-1" onClick={onClose}>Cancel</button><button className="btn btn-primary flex-1" onClick={submit} data-testid="nu-submit">Create</button></div>
      </div>
    </div>
  );
}
